import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import type { SaveProposalTemplatePayload } from "work/proposal-templates/_api/save-proposal-template-api-request";
import {
  type ProposalTemplateJSON,
  ProposalTemplateJSONZ,
} from "work/proposal-templates/_schema/proposal-template-schema";
import { z } from "zod";

const proposalTemplateListTag = {
  type: "ProposalTemplate",
  id: "LIST",
} as const;

/**
 * The caller's own saved proposal templates (#6129). Each is private to whoever saved it, so the
 * list is refreshed by the save and delete that change it, and by nothing else.
 */
export const proposalTemplateApi = api.injectEndpoints({
  endpoints: (build) => ({
    getProposalTemplates: build.query<ProposalTemplateJSON[], { search?: string }>({
      query: ({ search }) => ({
        url: "work/proposal-templates",
        params: search ? { search } : undefined,
      }),
      rawResponseSchema: z.array(ProposalTemplateJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: [proposalTemplateListTag],
    }),
    /**
     * One template, with who on its team cannot be carried over. Read when a draft is started and never
     * cached for long: whether somebody still works here is what changes between one start and the next.
     */
    getProposalTemplateById: build.query<ProposalTemplateJSON, { templateId: string }>({
      query: ({ templateId }) => `work/proposal-templates/${templateId}`,
      rawResponseSchema: ProposalTemplateJSONZ,
      onSchemaFailure: logSchemaFailure,
      keepUnusedDataFor: 0,
    }),
    saveProposalTemplate: build.mutation<ProposalTemplateJSON, SaveProposalTemplatePayload>({
      query: (payload) => ({
        url: "work/proposal-templates",
        method: "POST",
        body: payload,
      }),
      rawResponseSchema: ProposalTemplateJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: [proposalTemplateListTag],
    }),
    deleteProposalTemplate: build.mutation<void, { templateId: string }>({
      query: ({ templateId }) => ({
        url: `work/proposal-templates/${templateId}`,
        method: "DELETE",
      }),
      invalidatesTags: [proposalTemplateListTag],
    }),
  }),
});

export const {
  useGetProposalTemplatesQuery,
  useLazyGetProposalTemplateByIdQuery,
  useSaveProposalTemplateMutation,
  useDeleteProposalTemplateMutation,
} = proposalTemplateApi;
